/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

// Matches Hygraph's default CDNs and Epic's CNAME aliases (staging + prod), so that feed
// API responses that return prod URLs are still recognised in staging/local dev.
const HYGRAPH_HOST_PATTERNS = [/\.graphassets\.com$/, /(^|\.)cms-assets(-[\w-]+)?\.unrealengine\.com$/];

/**
 * Determines if a given src URL is a hygraph asset
 * @param hygraphMediaDomain
 * @param src
 * @returns boolean
 */
export const isHygraphImageSrc = (hygraphMediaDomain: string, src: string): boolean => {
	if (!src) return false;
	if (hygraphMediaDomain && src.includes(hygraphMediaDomain)) return true;
	// src may be a full URL or a bare hostname (getHygraphImageIds calls this with parts[2])
	let host = src;
	try {
		host = new URL(src).hostname;
	} catch {
		// leave host as the raw src (likely already a hostname)
	}
	return HYGRAPH_HOST_PATTERNS.some((re) => re.test(host));
};

interface HygraphImageIds {
	assetId: string;
	environmentId?: string;
}

/**
 * Attempts to parse hygraph asset URL and return the assetId, environmentId
 */
export const getHygraphImageIds = (hygraphMediaDomain: string, src: string): HygraphImageIds => {
	let assetId = "";
	let environmentId = "";

	if (!src) {
		return { assetId, environmentId };
	}

	// strip query string before parsing so suffixes like ?width=N don't mask the trailing id segment
	const pathOnly = src.split("?")[0] || src;
	const parts = pathOnly.split("/");

	if (parts.length && parts[2] && isHygraphImageSrc(hygraphMediaDomain, parts[2]) && parts.length > 3) {
		assetId = parts[3];

		//new system
		if (parts.length >= 5) {
			// remove output= resize=x: parts
			const cleanedIds = parts
				.slice(4)
				.filter((el) => el && !el.includes(":") && !el.includes("=") && el !== "auto_image");
			if (cleanedIds.length > 0) environmentId = cleanedIds[0];
		}
	}

	return { assetId, environmentId };
};

const HYGRAPH_TRANSFORM_MARKERS = ["resize=", "quality=value", "output=format", "/auto_image", "/process?"];

/**
 * Whether a URL string carries a Hygraph transformation marker (or is a
 * `/process?` endpoint URL) and therefore must not be rewritten. This is a bare
 * substring check — it does not validate that `src` is a Hygraph asset URL;
 * pair it with {@link isHygraphImageSrc}.
 */
export const hasHygraphTransformations = (src: string): boolean => {
	return HYGRAPH_TRANSFORM_MARKERS.some((marker) => src.includes(marker));
};

// Hygraph environment ids and asset handles are URL-safe alphanumerics; anything
// else (transform segments carry `=`/`:`) means the path isn't a bare asset URL.
const HYGRAPH_ID_SHAPE = /^[A-Za-z0-9_-]+$/;

/**
 * Builds a compressed variant (`quality=value:90/auto_image`) of a bare Hygraph
 * asset URL, mirroring the path-transform shape of `hygraphImageLoader`'s
 * no-width branch (without the `?width=N` suffix next/image requires). The
 * query string is preserved (as serialized by `URL` — verbatim for
 * already-encoded queries) and the returned URL is always `https`. Only
 * compression/format transforms are added — no `resize=` width variants,
 * since the rendered width is unknown at this level.
 *
 * Returns `""` (caller keeps the original) unless the src is a Hygraph asset
 * URL of exactly the `/{environmentId}/{handle}` shape (tolerating redundant
 * slashes, which the rewrite normalizes away) with no existing
 * transformations — rewriting anything else would corrupt the URL. In
 * particular, legacy single-segment `/{handle}` URLs use a transforms-first
 * grammar the rewritten shape 404s on, and fragment-bearing srcs are skipped
 * because `&#38;`-style entities in attribute values parse as fragments.
 *
 * Accepts protocol-relative (`//host/...`) srcs by assuming https. The origin
 * host is preserved rather than forced to the configured domain — CMS content
 * may carry prod URLs in staging/local envs.
 *
 * @param hygraphMediaDomain - The configured Hygraph media CDN domain. Built-in
 *        Hygraph host patterns are matched even when this is empty.
 * @param src - The image src URL to rewrite.
 * @returns The compressed URL, or `""` when the src can't be safely rewritten.
 */
export const buildHygraphCompressedImageUrl = (hygraphMediaDomain: string, src: string): string => {
	if (!src || src.includes("#")) return "";

	const absoluteSrc = src.startsWith("//") ? `https:${src}` : src;
	if (!isHygraphImageSrc(hygraphMediaDomain, absoluteSrc)) return "";
	if (hasHygraphTransformations(absoluteSrc)) return "";

	let url: URL;
	try {
		url = new URL(absoluteSrc);
	} catch {
		return "";
	}

	const pathSegments = url.pathname.split("/").filter(Boolean);
	if (
		pathSegments.length !== 2 ||
		pathSegments.some((s) => !HYGRAPH_ID_SHAPE.test(s)) ||
		pathSegments.includes("process")
	) {
		return "";
	}

	// The first isHygraphImageSrc call matches the configured domain anywhere in
	// the raw src (including query params) — re-check the parsed hostname so a
	// foreign host can't smuggle the domain through its query string.
	if (!isHygraphImageSrc(hygraphMediaDomain, url.hostname)) return "";

	const [environmentId, assetHandle] = pathSegments;
	return `https://${url.hostname}/${environmentId}/quality=value:90/auto_image/${assetHandle}${url.search}`;
};
